import { getDefaultRate } from './taxes.js';
import { resolveAuthenticatedHeaders } from '@/utils/requestContext.js';

function resolveApiBase() {
  const configuredBase = String(import.meta.env.VITE_API_URL || '').trim();
  if (configuredBase) {
    return configuredBase.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined') {
    const hostname = String(window.location.hostname || '').trim().toLowerCase();
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:4000';
    }
  }

  return 'https://api.sinamonsoft.ar';
}

const API_BASE = resolveApiBase();
const isDev = Boolean(import.meta.env.DEV);

function summarizePayload(payload) {
  return {
    clientId: payload?.client?.id || null,
    clientName: payload?.client?.razonSocial || payload?.client?.razon || null,
    salePoint: payload?.payment?.salePoint || null,
    saleCondition: payload?.payment?.saleCondition || null,
    currency: payload?.payment?.currency || null,
    voucherType: payload?.payment?.voucherType || null,
    items: Array.isArray(payload?.items)
      ? payload.items.map((item) => ({
        codigo: item?.codigo || null,
        descripcion: item?.descripcion || null,
        qty: Number(item?.qty || 0),
        price: Number(item?.price || 0),
        iva: Number(item?.iva || 0),
      }))
      : [],
    totals: payload?.totals
      ? {
        net: Number(payload.totals.net || 0),
        ivaByRate: payload.totals.ivaByRate || {},
        total: Number(payload.totals.total || 0),
      }
      : null,
  };
}

function logDev(event, details) {
  if (!isDev) return;
  console.info(`[facturacion-manual] ${event}`, details);
}

async function readResponsePayload(response) {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  const rawText = await response.text();
  let body = {};

  if (rawText) {
    if (contentType.includes('application/json')) {
      try {
        body = JSON.parse(rawText);
      } catch {
        body = { raw: rawText };
      }
    } else {
      body = { raw: rawText };
    }
  }

  return { contentType, rawText, body };
}

function buildFriendlyRequestError(error, fallbackMessage) {
  const payload = error?.payload || {};
  const payloadError = payload.error;
  const nestedError = payloadError && typeof payloadError === 'object' ? payloadError : {};
  const code = payload.code || nestedError.code || error?.code || '';
  const backendMessage = [
    payload.message,
    payload.mensaje,
    typeof payloadError === 'string' ? payloadError : null,
    nestedError.message,
    payload.msg,
  ].find((message) => typeof message === 'string' && message.trim()) || '';
  const backendObs = payload.obs || nestedError.obs || nestedError.hint;

  if (error?.name === 'TypeError' || /Failed to fetch/i.test(String(error?.message || ''))) {
    const failure = new Error('No se pudo conectar con el servicio de facturación. Verificá conexión/API o intentá nuevamente.');
    failure.code = 'NETWORK_ERROR';
    failure.cause = error;
    return failure;
  }

  if (code === 'PHP_NOT_AVAILABLE' || code === 'AFIP_PHP_EXEC_ERROR' || code === 'AFIP_PHP_SCRIPT_MISSING') {
    const failure = new Error('No se pudo conectar con el servicio de facturación. Verificá conexión/API o intentá nuevamente.');
    failure.code = code;
    failure.cause = error;
    failure.details = backendMessage || null;
    return failure;
  }

  const failure = new Error(backendMessage || error?.message || fallbackMessage);
  failure.code = code || 'REQUEST_ERROR';
  failure.cause = error;
  failure.details = backendObs || null;
  failure.payload = payload;
  return failure;
}

export async function saveDraft(payload) {
  const endpoint = `${API_BASE}/api/facturas/manual/borrador`;
  const payloadSummary = summarizePayload(payload);
  logDev('draft-request', { endpoint, payload: payloadSummary });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: resolveAuthenticatedHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });

    const parsed = await readResponsePayload(response);
    logDev('draft-response', {
      endpoint,
      status: response.status,
      ok: response.ok,
      body: parsed.body,
    });

    if (!response.ok || parsed.body?.success === false || parsed.body?.ok === false) {
      const error = new Error(parsed.body?.mensaje || parsed.body?.message || parsed.body?.error || 'No se pudo guardar el borrador.');
      error.payload = parsed.body;
      error.status = response.status;
      throw error;
    }

    return {
      ok: true,
      draftId: parsed.body?.draftId || parsed.body?.facturaId || parsed.body?.factura_id || null,
      facturaId: parsed.body?.facturaId || parsed.body?.factura_id || null,
      number: parsed.body?.numero_formateado || parsed.body?.numero || '',
      message: parsed.body?.mensaje || parsed.body?.message || 'Borrador guardado correctamente.',
    };
  } catch (error) {
    logDev('draft-error', {
      endpoint,
      payload: payloadSummary,
      error,
      responsePayload: error?.payload || null,
    });
    throw buildFriendlyRequestError(error, 'No se pudo guardar el borrador.');
  }
}

export async function discardDraft(id) {
  const response = await fetch(`${API_BASE}/api/facturas/manual/borradores/${id}`, {
    method: 'DELETE',
    headers: resolveAuthenticatedHeaders({ 'Content-Type': 'application/json' }),
  });
  const parsed = await readResponsePayload(response);
  if (!response.ok || parsed.body?.success === false || parsed.body?.ok === false) {
    const error = new Error(parsed.body?.mensaje || parsed.body?.message || 'No se pudo descartar el borrador.');
    error.payload = parsed.body;
    error.status = response.status;
    throw error;
  }
  return parsed.body;
}

export async function issue(payload) {
  const endpoint = `${API_BASE}/api/facturas/manual/emitir`;
  const payloadSummary = summarizePayload(payload);
  logDev('issue-request', { endpoint, payload: payloadSummary });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: resolveAuthenticatedHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });

    const parsed = await readResponsePayload(response);
    logDev('issue-response', {
      endpoint,
      status: response.status,
      ok: response.ok,
      body: parsed.body,
    });

    if (!response.ok || parsed.body?.success === false || parsed.body?.ok === false) {
      const error = new Error(parsed.body?.mensaje || parsed.body?.message || parsed.body?.error || 'No se pudo emitir la factura.');
      error.payload = parsed.body;
      error.status = response.status;
      throw error;
    }

    return {
      ok: true,
      facturaId: parsed.body?.facturaId || parsed.body?.factura_id || null,
      number: parsed.body?.numero_formateado || parsed.body?.numero || '',
      numberRaw: parsed.body?.numero || null,
      letra: parsed.body?.letra || '',
      ptovta: parsed.body?.ptovta || '',
      tipoCbte: parsed.body?.tipo_cbte || null,
      cae: parsed.body?.cae || '',
      caeVto: parsed.body?.vencimiento_cae || '',
      message: parsed.body?.mensaje || parsed.body?.message || 'Factura autorizada correctamente.',
    };
  } catch (error) {
    logDev('issue-error', {
      endpoint,
      payload: payloadSummary,
      error,
      responsePayload: error?.payload || null,
    });
    throw buildFriendlyRequestError(error, 'No se pudo emitir la factura.');
  }
}

export function openInvoicePdfPlaceholder() {
  // Open during the click event so browsers do not treat the eventual PDF as a popup.
  const popup = window.open('', '_blank');
  if (!popup) return null;

  try {
    popup.opener = null;
  } catch {
    // Some browsers disallow changing opener; the PDF can still be shown.
  }

  popup.document.write(`
    <!doctype html>
    <html lang="es">
      <head><meta charset="utf-8" /><title>Factura PDF</title></head>
      <body style="font-family:system-ui;padding:24px">Cargando factura…</body>
    </html>
  `);
  popup.document.close();
  return popup;
}

function invoicePdfFilename(contentDisposition, facturaId) {
  const encodedFilename = String(contentDisposition || '').match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plainFilename = String(contentDisposition || '').match(/filename\s*=\s*(?:"([^"]+)"|([^;\s]+))/i);
  const serverFilename = encodedFilename
    ? decodeURIComponent(encodedFilename)
    : plainFilename?.[1] || plainFilename?.[2] || '';
  const safeFilename = serverFilename.replace(/[\\/:*?"<>|\r\n]/g, '').trim();
  if (safeFilename) return /\.pdf$/i.test(safeFilename) ? safeFilename : `${safeFilename}.pdf`;
  return `FACTURA-${facturaId}.pdf`;
}

function renderInvoicePdfPreview(popup, objectUrl, filename) {
  // A PDF loaded from a blob URL has no filename, so the browser PDF viewer
  // falls back to the blob UUID. Keep the preview, but provide its own
  // download action with the server-provided invoice number.
  popup.document.open();
  popup.document.write(`
    <!doctype html>
    <html lang="es">
      <head><meta charset="utf-8" /><title>${filename}</title></head>
      <body style="margin:0;background:#f5f5f5;font-family:system-ui,sans-serif">
        <header style="display:flex;align-items:center;gap:12px;padding:10px 16px;background:#fff;border-bottom:1px solid #ddd">
          <strong style="font-size:14px">${filename}</strong>
          <a id="invoice-pdf-download" style="margin-left:auto;padding:8px 12px;border-radius:6px;background:#1d4ed8;color:#fff;text-decoration:none;font-size:14px;font-weight:600">Descargar PDF</a>
        </header>
        <iframe id="invoice-pdf-preview" title="Vista previa de factura" style="display:block;width:100%;height:calc(100vh - 59px);border:0"></iframe>
      </body>
    </html>
  `);
  popup.document.close();

  const downloadLink = popup.document.getElementById('invoice-pdf-download');
  const preview = popup.document.getElementById('invoice-pdf-preview');
  if (downloadLink) {
    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    downloadLink.textContent = `Descargar ${filename}`;
  }
  if (preview) preview.src = objectUrl;
}

export async function loadInvoicePdfInWindow(facturaId, popup) {
  if (!popup || !facturaId) return;

  try {
    const response = await fetch(`${API_BASE}/api/facturas/${encodeURIComponent(facturaId)}/pdf`, {
      headers: resolveAuthenticatedHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.message || payload?.error || 'No se pudo abrir el PDF de la factura.');
    }

    const filename = invoicePdfFilename(response.headers.get('content-disposition'), facturaId);
    const objectUrl = URL.createObjectURL(await response.blob());
    renderInvoicePdfPreview(popup, objectUrl, filename);
    popup.addEventListener(
      'beforeunload',
      () => window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000),
      { once: true },
    );
  } catch (error) {
    popup.close();
    throw error;
  }
}

export async function getManualInvoiceConfiguration(voucherType) {
  const endpoint = `${API_BASE}/api/facturas/manual/configuracion?${new URLSearchParams({
    tipo_comprobante: voucherType,
  }).toString()}`;

  const response = await fetch(endpoint, {
    headers: resolveAuthenticatedHeaders(),
  });
  const parsed = await readResponsePayload(response);
  if (!response.ok || parsed.body?.success === false || parsed.body?.ok === false) {
    const error = new Error(parsed.body?.message || parsed.body?.error || 'No se pudo resolver el punto de venta.');
    error.payload = parsed.body;
    error.status = response.status;
    throw buildFriendlyRequestError(error, 'No se pudo resolver el punto de venta.');
  }
  return parsed.body?.data || {};
}

async function getManualInvoiceTemplateRequest(endpoint, fallbackMessage) {
  const response = await fetch(endpoint, { headers: resolveAuthenticatedHeaders() });
  const parsed = await readResponsePayload(response);
  if (!response.ok || parsed.body?.success === false || parsed.body?.ok === false) {
    const error = new Error(parsed.body?.message || parsed.body?.mensaje || parsed.body?.error || fallbackMessage);
    error.payload = parsed.body;
    error.status = response.status;
    throw buildFriendlyRequestError(error, fallbackMessage);
  }
  return parsed.body?.data ?? parsed.body;
}

export async function getManualInvoiceTemplates(filters = {}) {
  const params = new URLSearchParams();
  const fields = ['cliente_id', 'fecha_desde', 'fecha_hasta', 'numero', 'texto', 'page', 'page_size'];
  fields.forEach(field => {
    const value = filters[field];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      params.set(field, String(value));
    }
  });
  return getManualInvoiceTemplateRequest(
    `${API_BASE}/api/facturas/manual/plantillas?${params.toString()}`,
    'No se pudieron cargar las facturas manuales.',
  );
}

export async function getManualInvoiceTemplateDetail(invoiceId) {
  return getManualInvoiceTemplateRequest(
    `${API_BASE}/api/facturas/manual/plantillas/${encodeURIComponent(invoiceId)}/detalle`,
    'No se pudo cargar el detalle de la factura.',
  );
}

export function computeItemSubtotal({ qty = 0, price = 0, discPct = 0 }) {
  const numericQty = Number(qty) || 0;
  const numericPrice = Number(price) || 0;
  const line = numericQty * numericPrice;
  const discount = Math.min(Math.max(Number(discPct) || 0, 0), 100);
  const subtotal = line - line * (discount / 100);
  return Number.isFinite(subtotal) ? subtotal : 0;
}

export function computeTaxesByRate(items = []) {
  return items.reduce((acc, item) => {
    const rate = Number(item?.iva ?? getDefaultRate()) || 0;
    const subtotal = computeItemSubtotal(item);
    if (!acc[rate]) {
      acc[rate] = { base: 0, iva: 0 };
    }
    acc[rate].base += subtotal;
    acc[rate].iva = acc[rate].base * (rate / 100);
    return acc;
  }, {});
}

export function roundDownToHundred(value) {
  return Math.floor((Number(value) || 0) / 100) * 100;
}

const money = value => Number((Number(value) || 0).toFixed(2));
const toCents = value => Math.round(money(value) * 100);
const fromCents = value => value / 100;
const ROUNDING_FALLBACK_VAT_RATES = new Set([10.5, 21, 27]);

function applyPreviewRounding(taxesByRate, otherTaxesTotal, target) {
  const entries = Object.entries(taxesByRate)
    .sort(([, left], [, right]) => Number(right?.base || 0) - Number(left?.base || 0));
  if (!entries.length) return null;

  const [selectedRate, selected] = entries[0];
  const rate = Number(selectedRate) || 0;
  const otherTotal = money(entries
    .filter(([entryRate]) => entryRate !== selectedRate)
    .reduce((sum, [, entry]) => sum + Number(entry.base || 0) + Number(entry.iva || 0), otherTaxesTotal));
  const base = money(selected.base);
  const estimateCents = Math.round(((target - otherTotal) / (1 + rate / 100) - base) * 100);
  const targetCents = toCents(target);
  const otherTotalCents = toCents(otherTotal);
  const baseCents = toCents(base);
  let fallback = null;

  for (let distance = 0; distance <= 250; distance += 1) {
    const offsets = distance === 0 ? [0] : [-distance, distance];
    for (const offset of offsets) {
      const adjustedBaseCents = baseCents + estimateCents + offset;
      if (adjustedBaseCents < 0) continue;
      const adjustedBase = fromCents(adjustedBaseCents);
      const calculatedIva = money(adjustedBase * rate / 100);
      const calculatedIvaCents = toCents(calculatedIva);
      const residualCents = targetCents - otherTotalCents - adjustedBaseCents - calculatedIvaCents;

      if (residualCents === 0) {
        const ivaByRate = { ...taxesByRate, [selectedRate]: { base: adjustedBase, iva: calculatedIva } };
        return {
          ivaByRate,
          net: money(Object.values(ivaByRate).reduce((sum, entry) => sum + Number(entry.base || 0), 0)),
          baseRoundingAdjustment: money(adjustedBase - base),
        };
      }

      if (
        !fallback
        && ROUNDING_FALLBACK_VAT_RATES.has(rate)
        && Math.abs(residualCents) <= 1
      ) {
        const adjustedIvaCents = targetCents - otherTotalCents - adjustedBaseCents;
        if (adjustedIvaCents >= 0 && Math.abs(adjustedIvaCents - calculatedIvaCents) <= 1) {
          fallback = { base: adjustedBase, iva: fromCents(adjustedIvaCents) };
        }
      }
    }
  }

  if (fallback) {
    const ivaByRate = { ...taxesByRate, [selectedRate]: fallback };
    return {
      ivaByRate,
      net: money(Object.values(ivaByRate).reduce((sum, entry) => sum + Number(entry.base || 0), 0)),
      baseRoundingAdjustment: money(fallback.base - base),
    };
  }
  return null;
}

export function computeTotals({ items = [], globalDiscPct = 0, globalDiscAmount = 0, otherTaxes = [], roundToHundred = false } = {}) {
  const taxesByRate = computeTaxesByRate(items);
  const netBeforeDiscount = Object.values(taxesByRate).reduce((sum, entry) => sum + entry.base, 0);
  const pctDiscountValue = netBeforeDiscount * (Math.max(Number(globalDiscPct) || 0, 0) / 100);
  const amountDiscountValue = Math.max(Number(globalDiscAmount) || 0, 0);
  const selectedDiscount = globalDiscPct ? pctDiscountValue : amountDiscountValue;
  const discount = Math.min(selectedDiscount, netBeforeDiscount);
  const netAfterDiscount = netBeforeDiscount - discount;
  const ivaByRate = Object.entries(taxesByRate).reduce((acc, [rate, entry]) => {
    const proportion = netBeforeDiscount > 0 ? entry.base / netBeforeDiscount : 0;
    const base = entry.base - discount * proportion;
    const numericRate = Number(rate) || 0;
    acc[rate] = {
      base,
      iva: base * (numericRate / 100),
    };
    return acc;
  }, {});
  const otherTaxesTotal = otherTaxes.reduce((sum, tax) => sum + (Number(tax.amount) || 0), 0);
  const calculatedTotal =
    netAfterDiscount +
    Object.values(ivaByRate).reduce((sum, entry) => sum + entry.iva, 0) +
    otherTaxesTotal;
  const target = roundToHundred ? roundDownToHundred(calculatedTotal) : calculatedTotal;
  const rounded = roundToHundred && target !== calculatedTotal
    ? applyPreviewRounding(ivaByRate, otherTaxesTotal, target)
    : null;
  if (roundToHundred && target !== calculatedTotal && !rounded) {
    throw new Error('No se pudo distribuir el ajuste de redondeo antes del IVA.');
  }
  const finalIvaByRate = rounded?.ivaByRate || ivaByRate;
  const finalNet = rounded?.net ?? netAfterDiscount;
  const total = roundToHundred ? target : calculatedTotal;

  return {
    netBeforeDiscount,
    net: finalNet,
    discount,
    ivaByRate: finalIvaByRate,
    otherTaxesTotal,
    total,
    calculatedTotal,
    // Preserve the UI's gross adjustment; expose the pre-VAT item-base delta separately.
    roundingAdjustment: money(total - calculatedTotal),
    baseRoundingAdjustment: rounded?.baseRoundingAdjustment || 0,
  };
}

export function formatGlobalDiscountLabel(percent) {
  const value = Number(percent);
  return percent === null || percent === undefined || !Number.isFinite(value) || value <= 0
    ? 'Descuento global'
    : `Descuento global (${String(value).replace('.', ',')}%)`;
}

// Economic rows shown above "Neto gravado" when a global discount exists.
// `subtotal` is the pre-discount net (it includes the rounding base, so that
// subtotal - discount = net). Returns [] when there is nothing to show.
export function buildGlobalDiscountRows({ net = 0, discount = 0, percent = null } = {}) {
  const discountAmount = money(Math.max(Number(discount) || 0, 0));
  if (!discountAmount) return [];
  return [
    ['Subtotal sin IVA', money((Number(net) || 0) + discountAmount)],
    [formatGlobalDiscountLabel(percent), -discountAmount],
  ];
}
